/**
 * pricing.js, market-adaptive pricing for Keystone by CloudVoro.
 *
 * Design intent (see the README-level pricing rationale):
 *   - EN (Ireland / UK) and DE (Germany, DACH), headline EUR pricing.
 *     DACH pays for perceived quality; no discount there.
 *   - PL and HU, priced in local currency at ~32% below the EU headline.
 *     Not so cheap that a compliance buyer thinks "hobby tool", not so
 *     high that it kills conversion in a lower-ARPU market.
 *
 * Every price component reads its numbers from one function:
 *   getPricing(lang) → { symbol, code, standardMonthly, ... }
 *
 * Every string returned is fully formatted (currency symbol + thousands
 * separator) so the calling component just interpolates {p.standardMonthlyFmt}.
 */
import { getStoredLang } from './langDetect';

// Formatters, reuse Intl.NumberFormat under the hood; safe on all evergreen browsers.
const fmt = (n, code, locale) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency', currency: code, maximumFractionDigits: 0, minimumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${code} ${n.toLocaleString(locale)}`;
  }
};

// PL and HU number formatting uses local grouping ("2 290" vs "2,290"),
// which is more familiar to buyers in those markets.
const PRICING = {
  en: {
    code: 'EUR', symbol: '€', locale: 'en-IE',
    standardMonthly: 790, plusMonthly: 1290,
    annualStandard: 8058, annualSavings: 1422,
    pilotFee: 2500, pilotRefund: 1250,
    implMin: 4950, implMax: 16500,
    dayRate: 850, extraTrainingDay: 750, customDispatchTemplate: 350,
    multiSiteAddonMonthly: 500, perDayFactoryCost: 26,
    boltOnAccountsApi: 2800, boltOnRetailerEDI: 1800,
    hourlyStaffCost: 25, avgOrderValue: 850, auditIncidentCost: 18500,
    annualStaffSalary: 40000, annualKeystoneCost: 9480,
  },
  de: {
    // Same as EN, DACH pays the headline.
    code: 'EUR', symbol: '€', locale: 'de-DE',
    standardMonthly: 790, plusMonthly: 1290,
    annualStandard: 8058, annualSavings: 1422,
    pilotFee: 2500, pilotRefund: 1250,
    implMin: 4950, implMax: 16500,
    dayRate: 850, extraTrainingDay: 750, customDispatchTemplate: 350,
    multiSiteAddonMonthly: 500, perDayFactoryCost: 26,
    boltOnAccountsApi: 2800, boltOnRetailerEDI: 1800,
    hourlyStaffCost: 25, avgOrderValue: 850, auditIncidentCost: 18500,
    annualStaffSalary: 40000, annualKeystoneCost: 9480,
  },
  pl: {
    // ~32% below EU headline. Sits above "toy tool" (Comarch Optima Start,
    // inFakt at 500-1200 PLN) and well below full ERP (SAP B1, Comarch XL at 5000+ PLN).
    code: 'PLN', symbol: 'PLN', locale: 'pl-PL',
    standardMonthly: 2290, plusMonthly: 3690,
    annualStandard: 23358, annualSavings: 4122,
    pilotFee: 6900, pilotRefund: 3450,
    implMin: 14900, implMax: 49900,
    dayRate: 2490, extraTrainingDay: 2190, customDispatchTemplate: 990,
    multiSiteAddonMonthly: 1490, perDayFactoryCost: 76,
    boltOnAccountsApi: 8200, boltOnRetailerEDI: 5300,
    hourlyStaffCost: 75, avgOrderValue: 2490, auditIncidentCost: 54000,
    annualStaffSalary: 118000, annualKeystoneCost: 27480,
  },
  hu: {
    // Same positioning as PL, in HUF. HUF is grouped locally as "219 000".
    code: 'HUF', symbol: 'Ft', locale: 'hu-HU',
    standardMonthly: 219000, plusMonthly: 359000,
    annualStandard: 2233800, annualSavings: 394200,
    pilotFee: 690000, pilotRefund: 345000,
    implMin: 1490000, implMax: 4990000,
    dayRate: 249000, extraTrainingDay: 219000, customDispatchTemplate: 99000,
    multiSiteAddonMonthly: 149000, perDayFactoryCost: 7600,
    boltOnAccountsApi: 820000, boltOnRetailerEDI: 530000,
    hourlyStaffCost: 7500, avgOrderValue: 249000, auditIncidentCost: 5400000,
    annualStaffSalary: 11800000, annualKeystoneCost: 2628000,
  },
};

/**
 * Returns the pricing config for a given language (`en` | `de` | `pl` | `hu`),
 * with `Fmt` string versions of every numeric value ready for the DOM.
 */
export function getPricing(lang) {
  const raw = PRICING[lang] || PRICING.en;
  const F = (n) => fmt(n, raw.code, raw.locale);

  // Founder's Club, 30% off, locked for 24 months. Applies to the
  // Standard licence only. Monthly rate rounds to a clean-looking figure
  // in each currency; annual is derived from monthly × 12 (no additional
  // prepay stacking, the 30% already beats the 15% prepay discount).
  const rounder = raw.code === 'HUF' ? 1000 : (raw.code === 'PLN' ? 10 : 1);
  const founderMonthly = Math.round((raw.standardMonthly * 0.7) / rounder) * rounder;
  const founderAnnual = founderMonthly * 12;

  return {
    ...raw,
    lang,
    standardMonthlyFmt: F(raw.standardMonthly),
    plusMonthlyFmt: F(raw.plusMonthly),
    annualStandardFmt: F(raw.annualStandard),
    annualSavingsFmt: F(raw.annualSavings),
    pilotFeeFmt: F(raw.pilotFee),
    pilotRefundFmt: F(raw.pilotRefund),
    implMinFmt: F(raw.implMin),
    implMaxFmt: F(raw.implMax),
    dayRateFmt: F(raw.dayRate),
    extraTrainingDayFmt: F(raw.extraTrainingDay),
    customDispatchTemplateFmt: F(raw.customDispatchTemplate),
    multiSiteAddonMonthlyFmt: F(raw.multiSiteAddonMonthly),
    perDayFactoryCostFmt: F(raw.perDayFactoryCost),
    boltOnAccountsApiFmt: F(raw.boltOnAccountsApi),
    boltOnRetailerEDIFmt: F(raw.boltOnRetailerEDI),
    hourlyStaffCostFmt: F(raw.hourlyStaffCost),
    avgOrderValueFmt: F(raw.avgOrderValue),
    auditIncidentCostFmt: F(raw.auditIncidentCost),
    annualStaffSalaryFmt: F(raw.annualStaffSalary),
    annualKeystoneCostFmt: F(raw.annualKeystoneCost),
    founderMonthly, founderAnnual,
    founderMonthlyFmt: F(founderMonthly),
    founderAnnualFmt: F(founderAnnual),
    isLocalMarket: lang === 'pl' || lang === 'hu',
  };
}

/**
 * React hook, reads the current site language (URL prefix wins, otherwise
 * the localStorage choice) and returns fully formatted pricing.
 */
export function usePricing() {
  // URL prefix takes precedence for statically-linked landing pages, else
  // fall back to the user's saved choice (drives EN/DE/PL/HU on main pages).
  const m = typeof window !== 'undefined'
    ? window.location.pathname.match(/^\/(hu|pl|de)(\/|$)/)
    : null;
  const lang = m ? m[1] : (getStoredLang() || 'en');
  return getPricing(lang);
}
